import { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { formatCurrency } from '../../lib/currency';

const REASONS = {
  archived: 'Gearchiveerd', duplicate_blocked: 'Geblokkeerd als duplicaat',
  'status:parsed': 'Nog niet gevalideerd (geparsed)', 'status:needs_review': 'Wacht op beoordeling',
  external_resolution_registered: 'Extern afgehandeld (geregistreerd)',
};
const STATUS = {
  parsed: 'Geparsed', needs_review: 'Te beoordelen', confirmed: 'Bevestigd', approved: 'Goedgekeurd', exported: 'Geëxporteerd',
  export_failed: 'Export mislukt', duplicate_blocked: 'Duplicaat geblokkeerd',
};
const ERR = {
  validation: 'validatiefout', config_error: 'configuratiefout', network_error: 'netwerkfout', remote_rejection: 'afgewezen door Yuki',
  rate_limit_error: 'quotum bereikt', soap_error: 'Yuki-fout', unknown: 'onbekende fout',
};

export function reasonLabel(reason) {
  if (!reason) return '';
  if (REASONS[reason]) return REASONS[reason];
  if (reason.startsWith('export_failed:')) {
    const [, err, tail] = reason.split(':');
    const e = ERR[err] || err;
    if (tail === 'validation_not_established') return `Export mislukt (${e}) — validatie niet vastgesteld`;
    if (tail === 'operator_validation_logged') return `Export mislukt (${e}) — eerder goedgekeurd`;
    if (tail === 'earlier_export_succeeded') return `Export mislukt (${e}) — eerder wel geëxporteerd`;
  }
  if (reason.startsWith('status_unknown:')) return `Onbekende status "${reason.split(':')[1]}"`;
  if (reason.startsWith('status:')) return STATUS[reason.split(':')[1]] || reason;
  return reason;
}
export const statusLabel = (s) => STATUS[s] || s || '—';
export const sourceLabel = (s) => ({ native_order: 'Webshop', import_pdf: 'PDF-import' }[s] || s || '—');
export const currencyLabel = (c) => (!c || c === 'UNKNOWN' ? 'Valuta onbekend' : c);

export function fmtAmount(v, cur = 'EUR') {
  const n = Number(v);
  if (cur === 'EUR') return formatCurrency(n);
  const txt = n.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return !cur || cur === 'UNKNOWN' ? `${txt} (valuta onbekend)` : `${txt} ${cur}`;
}

export function localTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('nl-BE', { dateStyle: 'medium', timeStyle: 'short' });
}

export function shortRange(start, end) {
  const f = (s) => { const d = new Date(s + 'T00:00:00'); return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' }); };
  return `${f(start)} – ${f(end)}`;
}

/** Accessible popover: opens on hover, keyboard focus and click; closes on blur/Escape/outside click. */
export function HintIcon({ text, kind = 'warning', label, testid }) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || focus || pinned;
  const id = useId();
  const ref = useRef(null);
  useEffect(() => {
    if (!pinned) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setPinned(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setPinned(false); setFocus(false); } };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [pinned]);
  const Icon = kind === 'warning' ? AlertTriangle : Info;
  const color = kind === 'warning' ? 'text-amber-600 hover:text-amber-700' : 'text-slate-400 hover:text-slate-600';
  return (
    <span ref={ref} className="relative inline-flex align-middle" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" data-testid={testid} aria-label={label || text} aria-expanded={open} aria-describedby={open ? id : undefined}
        onClick={(e) => { e.stopPropagation(); setPinned((p) => !p); }} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        className={`inline-flex items-center justify-center h-5 w-5 rounded focus:outline-none focus:ring-2 focus:ring-amber-400 ${color}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <span role="tooltip" id={id} data-testid={testid ? `${testid}-popover` : undefined}
          className="absolute z-30 right-0 top-6 w-64 rounded-md border bg-white text-slate-800 shadow-lg p-2 text-xs leading-snug text-left whitespace-normal">
          {text}
        </span>
      )}
    </span>
  );
}

/**
 * Amount with completeness state. missing = records in the set without this value; total = records in the set.
 * complete → amount; partial → amount + warning icon; all missing → em dash + icon; null value → em dash + icon.
 */
export function Amount({ v, cur = 'EUR', missing = 0, total = 0, what = 'bedrag', strong = false, testid }) {
  const cls = `whitespace-nowrap tabular-nums ${strong ? 'font-semibold' : ''}`;
  if (v === null || v === undefined || v === '') {
    return <span className={cls} data-testid={testid} data-state="missing">— <HintIcon text={`Onbekend: ${what} ontbreekt of is ongeldig in de bron.`} label={`${what} ontbreekt`} /></span>;
  }
  if (missing > 0 && total > 0 && missing >= total) {
    return <span className={cls} data-testid={testid} data-state="unknown">— <HintIcon text={`Onbekend: ${what} ontbreekt bij alle ${total} facturen in deze set.`} label={`${what} onbekend`} /></span>;
  }
  if (missing > 0) {
    return (
      <span className={cls} data-testid={testid} data-state="partial">{fmtAmount(v, cur)} <HintIcon
        text={`Gedeeltelijk bedrag: ${what} ontbreekt bij ${missing} van ${total} facturen. Getoond is de som over de ${total - missing} facturen waar het bekend is.`}
        label={`Gedeeltelijk: ${what} ontbreekt bij ${missing} facturen`} /></span>
    );
  }
  return <span className={cls} data-testid={testid} data-state="complete">{fmtAmount(v, cur)}</span>;
}

export function BreakdownMark({ r }) {
  if (r?.vatBreakdownStatus === 'zero_invoice_verified') {
    return <span className="inline-flex items-center gap-1 ml-1 whitespace-nowrap text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`zero-invoice-${r.invoiceNumber}`}>Nulfactuur
      <HintIcon kind="info" text="Brongeverifieerde nulfactuur: alle bedragen in het origineel zijn € 0,00 (incl. beide BTW-tarieven). Er is geen BTW-uitsplitsing om te tonen; dit is geen ontbrekend gegeven." label="Nulfactuur, brongeverifieerd" /></span>;
  }
  if (r?.vatBreakdownStatus === 'missing') {
    return <HintIcon text="BTW-uitsplitsing per tarief ontbreekt in de opslag; het BTW-totaal is wel bekend. Deze factuur telt niet mee in BTW 6% / 21%." label="BTW-uitsplitsing ontbreekt" testid={`breakdown-missing-${r.invoiceNumber}`} />;
  }
  return null;
}

export function YukiBadge({ y }) {
  const cls = { exported: 'bg-emerald-100 text-emerald-800', pending: 'bg-blue-100 text-blue-800', failed: 'bg-red-100 text-red-800',
    external: 'bg-slate-200 text-slate-800', not_offered: 'bg-gray-100 text-gray-700', unknown: 'bg-gray-100 text-gray-700' }[y?.code] || 'bg-gray-100';
  return <span className={`whitespace-nowrap text-xs px-2 py-0.5 rounded ${cls}`} title={y?.exportedAt ? `Geëxporteerd op ${localTime(y.exportedAt)}` : ''}>{y?.label || 'Onbekend'}</span>;
}

export function Panel({ children, testid, className = '' }) {
  return <section data-testid={testid} className={`bg-white dark:bg-card border border-slate-200/80 rounded-xl shadow-sm p-5 md:p-6 ${className}`}>{children}</section>;
}

export function StatCard({ label, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200/80 bg-slate-50/60 px-3 py-2.5 min-w-0" data-testid={testid}>
      <div className="text-lg font-semibold leading-tight whitespace-nowrap">{children}</div>
      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{label}</div>
    </div>
  );
}

export function States({ loading, error, empty, emptyText, children }) {
  if (loading) return <div className="text-sm text-slate-500 py-6" data-testid="week-loading">Laden…</div>;
  if (error) return <div className="text-sm rounded border border-red-300 bg-red-50 p-3" data-testid="week-error">{String(error)}</div>;
  if (empty) return <div className="text-sm text-slate-600 border border-dashed rounded-lg p-6 text-center" data-testid="week-empty">{emptyText}{children}</div>;
  return null;
}

function RowList({ title, rows, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <details className="border border-slate-200/80 rounded-lg px-3 py-2" data-testid={testid}>
      <summary className="text-sm cursor-pointer text-slate-700">{title} <span className="text-slate-500">({rows.length}, niet meegeteld)</span></summary>
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-xs"><tbody>{rows.map((r) => (
          <tr key={r.id || r.invoiceNumber} className="border-t border-slate-100">
            <td className="p-1 font-mono whitespace-nowrap">{r.invoiceNumber || '—'}</td><td className="p-1 whitespace-nowrap">{r.invoiceDate || 'geen geldige datum'}</td>
            <td className="p-1">{r.customer}</td><td className="p-1 whitespace-nowrap">{statusLabel(r.status)}</td>
            <td className="p-1 text-right whitespace-nowrap">{r.gross == null ? '—' : fmtAmount(r.gross, r.currency)}</td>
            <td className="p-1 text-slate-500">{reasonLabel(r.reason)}</td></tr>))}</tbody></table>
      </div>
    </details>
  );
}

export function SideLists({ review, excluded, undated, undatedTotal, truncated }) {
  const extra = undatedTotal != null && undatedTotal !== (undated || []).length ? ` — hele administratie: ${undatedTotal}${truncated ? ', lijst afgekapt' : ''}` : '';
  return (
    <div className="space-y-2">
      <RowList title="Te beoordelen — validatie niet vastgesteld" rows={review} testid="week-review-list" />
      <RowList title="Uitgesloten" rows={excluded} testid="week-excluded-list" />
      <RowList title={`Zonder geldige factuurdatum${extra}`} rows={undated} testid="week-undated-list" />
    </div>
  );
}
